import { useEffect, useMemo, useState, useCallback } from "react";
import DraftModal from "../components/DraftModal.jsx";
import AppLayout from "../layouts/AppLayout.jsx";
import API from "../config/api.js";

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatMonthYearLabel(year, month) {
  return new Date(year, month - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function pad(value) {
  return String(value).padStart(2, "0");
}

function toYmd(year, month, day) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function isFutureDate(year, month, day) {
  const targetDate = new Date(year, month - 1, day);
  const now = new Date();
  return targetDate > now;
}

function buildMonthOptions() {
  const start = new Date(2023, 0, 1);
  const end = new Date();
  end.setMonth(end.getMonth() + 3);
  end.setDate(1);

  const options = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    const year = cursor.getFullYear();
    const month = cursor.getMonth() + 1;

    options.push({
      value: `${year},${pad(month)}`,
      year,
      month,
      label: formatMonthYearLabel(year, month),
    });

    cursor.setMonth(cursor.getMonth() + 1);
  }

  return options;
}

function getPlatformIcons(item) {
  return (
    <>
      {Number(item.facebook) === 1 && <i className="bi bi-facebook small ms-1"></i>}
      {Number(item.instagram) === 1 && <i className="bi bi-instagram small ms-1"></i>}
      {Number(item.linkedin) === 1 && <i className="bi bi-linkedin small ms-1"></i>}
    </>
  );
}

function getStatusBadge(item, isEditable) {
  if (isEditable) {
    return (
      <span className="badge bg-secondary text-primary shadow rounded-circle p-1 small" title="Scheduled">
        <i className="fi fi-rr-clock"></i>
      </span>
    );
  }

  const hasError =
    item.instagramautomationstatus === "error" ||
    item.facebookautomationstatus === "error" ||
    item.linkedinautomationstatus === "error";

  if (hasError) {
    return (
      <span className="badge bg-danger shadow rounded-circle p-1 small" title="Error">
        <i className="fi fi-rr-exclamation"></i>
      </span>
    );
  }

  return (
    <span className="badge bg-success shadow rounded-circle p-1 small" title="Posted">
      <i className="fi fi-rr-check"></i>
    </span>
  );
}

export default function MyCalendar() {
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const now = new Date();

  const currentMonthValue = `${now.getFullYear()},${pad(now.getMonth() + 1)}`;
  const currentOptionIndex = Math.max(
    0,
    monthOptions.findIndex((item) => item.value === currentMonthValue),
  );

  const [selectedIndex, setSelectedIndex] = useState(currentOptionIndex);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [selectedDraftId, setSelectedDraftId] = useState(null);

  const selectedOption = monthOptions[selectedIndex] || monthOptions[0];
  const year = selectedOption?.year || now.getFullYear();
  const month = selectedOption?.month || now.getMonth() + 1;
  const monthLabel = selectedOption?.label || formatMonthYearLabel(year, month);

  // ── useCallback so useEffect deps are stable ───────────────────────────────
  const loadCalendar = useCallback(async (targetMonth, targetYear) => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API.myCalendar}?list=true&month=${targetMonth}&year=${targetYear}`,
        { credentials: "include" },
      );
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao carregar o calendário:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []); // no external deps — uses only setters and API constant

  // ── Load when month/year changes ───────────────────────────────────────────
  useEffect(() => {
    loadCalendar(month, year);
  }, [month, year, loadCalendar]);

  // ── Register window._calendar while this page is mounted ──────────────────
  useEffect(() => {
    window._calendar = { refresh: () => loadCalendar(month, year) };
    return () => { delete window._calendar; };
  }, [loadCalendar, month, year]);

  // ── Derived state ──────────────────────────────────────────────────────────
  const itemsByDate = useMemo(() => {
    const grouped = {};
    items.forEach((item) => {
      const key = item?._userdate;
      if (!key) return;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    });
    return grouped;
  }, [items]);

  const calendarCells = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const totalCells = firstDay + daysInMonth;
    const remainingCells = (7 - (totalCells % 7)) % 7;
    const cells = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push({ type: "empty", key: `empty-start-${i}` });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = toYmd(year, month, day);
      cells.push({
        type: "day",
        key: date,
        day,
        date,
        editable: isFutureDate(year, month, day),
        items: itemsByDate[date] || [],
      });
    }

    for (let i = 0; i < remainingCells; i++) {
      cells.push({ type: "empty", key: `empty-end-${i}` });
    }

    return cells;
  }, [year, month, itemsByDate]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const openDraftPromptForDate = (date) => {
    const parts = date.split("-");
    const formattedDate = `${parts[1]}/${parts[2]}/${parts[0]}`;

    const modalElement = document.getElementById("draft-prompt");
    if (modalElement && window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(modalElement).show();
    }

    const scheduledCheckbox = document.querySelector('#draft-prompt [name="scheduled"]');
    if (scheduledCheckbox) {
      scheduledCheckbox.checked = true;
      scheduledCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
    }

    if (window.$ && typeof window.$ === "function") {
      try {
        window.$("#datepicker").datepicker("setDate", formattedDate);
      } catch (error) {
        console.error("Erro ao definir data no datepicker:", error);
      }
    }

    window._draft_prompt_check_if_is_today?.();
  };

  const updatePosition = async (iddraft, date) => {
    try {
      const formData = new FormData();
      formData.append("iddraft", iddraft);
      formData.append("date", date);
      const res = await fetch(`${API.myCalendar}?updateDate`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await res.json();
      if (data !== true) { console.error("Error: position not updated."); return false; }
      return true;
    } catch (error) {
      console.error("Erro na requisição:", error);
      return false;
    }
  };

  const handleDragStart = (iddraft) => setDraggedId(iddraft);
  const handleDragEnd = () => setDraggedId(null);

  const handleDrop = async (targetDate, editable) => {
    if (!draggedId) return;
    if (!editable) { window._alert?.("danger", "Select a valid date."); return; }
    const draggedItem = items.find((item) => item.iddraft === draggedId);
    if (!draggedItem) return;
    const success = await updatePosition(draggedId, targetDate);
    if (!success) return;
    setItems((prev) =>
      prev.map((item) =>
        item.iddraft === draggedId ? { ...item, _userdate: targetDate, date: targetDate } : item,
      ),
    );
  };

  const handleOpenDraft = (iddraft) => {
    setSelectedDraftId(iddraft);
    setDraftModalOpen(true);
  };

  const handleDraftEdited = useCallback(
    () => loadCalendar(month, year),
    [loadCalendar, month, year], // ← loadCalendar included
  );

  const handleDraftRemoved = useCallback((iddraft) => {
    setItems((prev) => prev.filter((item) => item.iddraft !== iddraft));
    setDraftModalOpen(false);
  }, []);

  const prevDisabled = selectedIndex <= 0;
  const nextDisabled = selectedIndex >= monthOptions.length - 1;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AppLayout currentPage="my-calendar">
      <style>{`
        .calendar-container-react {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 0;
        }
        .calendar-day-react.empty { background: #F7F5FD; }
        .calendar-label-react {
          padding: 14px;
          background: #ffffff;
          position: relative;
          outline: 1px solid #EAECF0;
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
          justify-content: center;
        }
        .calendar-day-react {
          min-height: 100px;
          padding-top: 35px !important;
          padding: 8px;
          background: #ffffff;
          position: relative;
          outline: 1px solid #EAECF0;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-content: flex-start;
        }
        .calendar-day-react.single-item { display: flex; justify-content: center; }
        .calendar-day-react.three-items { display: flex; flex-direction: column; align-items: center; }
        .calendar-day-react.four-or-more { display: grid; grid-template-columns: repeat(2, 1fr); }
        .calendar-post-react {
          padding: 5px;
          width: 100%;
          z-index: 1;
          position: relative;
          background-size: cover;
          background-position: center;
          cursor: pointer;
          min-height: 100px;
        }
        .calendar-post-react::after {
          content: "";
          position: absolute;
          top: 0; left: 0;
          width: 100%; height: 100%;
          background: rgba(0, 0, 0, 0.3);
          border-radius: 10px;
          z-index: -1;
        }
        .calendar-day-react.three-items .calendar-post-react { aspect-ratio: 2 / 1.2; }
        .calendar-add-button-react { aspect-ratio: 4 / 5; width: 100%; }
        .calendar-day-react:not(.editable) .calendar-add-button-react { visibility: hidden; }
      `}</style>

      <div className="container-fluid px-0">
        <div className="border bg-secondary-light rounded-top-4 border-bottom-0 p-2">
          <div className="row align-items-center justify-content-between">
            <div className="col-md-6 d-none d-md-block">
              <h2 className="mb-0 ms-2 fs-6">{monthLabel}</h2>
            </div>

            <div className="col-md-6 text-center text-md-end">
              <div className="border bg-white rounded-4 d-inline-block me-3" style={{ width: "230px" }}>
                <div className="row g-0">
                  <div className="col-2">
                    <button
                      type="button"
                      onClick={() => !prevDisabled && setSelectedIndex((prev) => prev - 1)}
                      className={`btn border-0 w-100 ${prevDisabled ? "disabled" : ""}`}
                    >
                      <i className="fi fi-rr-angle-left"></i>
                    </button>
                  </div>
                  <div className="col-8">
                    <span className="btn btn-white border-0 disabled w-100">{monthLabel}</span>
                  </div>
                  <div className="col-2">
                    <button
                      type="button"
                      onClick={() => !nextDisabled && setSelectedIndex((prev) => prev + 1)}
                      className={`btn border-0 w-100 ${nextDisabled ? "disabled" : ""}`}
                    >
                      <i className="fi fi-rr-angle-right"></i>
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="btn btn-primary rounded-4 m-2 px-4 d-none d-md-inline-block"
                onClick={() => window._draft_prompt_open?.()}
              >
                <span className="px-3">Add Post</span>
              </button>
            </div>
          </div>
        </div>

        <div style={{ border: "1px solid transparent" }}>
          <div className="calendar-container-react">
            {DAYS_OF_WEEK.map((day) => (
              <div key={day} className="calendar-label-react">{day}</div>
            ))}

            {calendarCells.map((cell) => {
              if (cell.type === "empty") {
                return <div key={cell.key} className="calendar-day-react empty"></div>;
              }

              const itemCount = cell.items.length;
              const extraClass =
                itemCount >= 4 ? "four-or-more"
                : itemCount === 3 ? "three-items"
                : itemCount === 1 ? "single-item"
                : "";

              return (
                <div
                  key={cell.key}
                  className={`calendar-day-react ${cell.editable ? "editable" : ""} ${extraClass}`}
                  data-date={cell.date}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleDrop(cell.date, cell.editable); }}
                >
                  <div className="position-absolute top-0 start-0 m-2 small text-muted">
                    {cell.day}
                  </div>

                  {cell.items.map((item) => (
                    <div
                      key={item.iddraft}
                      style={{ backgroundImage: `url(${API.baseUrl+'/'+item._thumbnail})`, animationDuration: "5s" }}
                      className="calendar-post-react position-relative small rounded-3 animate__animated"
                      data-id={item.iddraft}
                      draggable={cell.editable}
                      onDragStart={() => handleDragStart(item.iddraft)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleOpenDraft(item.iddraft)}
                    >
                      <span className="position-absolute small text-white m-1 top-0 start-0">
                        {item._usertime}
                      </span>
                      <span className="position-absolute small text-white m-1 top-0 end-0">
                        {getStatusBadge(item, cell.editable)}
                      </span>
                      <span className="position-absolute small text-white m-1 bottom-0 start-0">
                        <small className="small text-capitalize">{item.type}</small>
                      </span>
                      <span className="position-absolute small text-white m-1 bottom-0 end-0">
                        <small className="small">{getPlatformIcons(item)}</small>
                      </span>
                      {item.type === "reel" && (
                        <span className="position-absolute top-50 start-50 translate-middle text-white">
                          <i className="fi fi-sr-play"></i>
                        </span>
                      )}
                    </div>
                  ))}

                  {cell.items.length === 0 && (
                    <button
                      type="button"
                      className="btn btn-outline-primary mh-100 d-none d-sm-block calendar-add-button-react border-0"
                      onClick={() => openDraftPromptForDate(cell.date)}
                    >
                      <i className="bi bi-plus-lg text-white fs-1"></i>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border" role="status" aria-hidden="true"></div>
          </div>
        )}
      </div>

      <DraftModal
        iddraft={selectedDraftId}
        open={draftModalOpen}
        onClose={() => setDraftModalOpen(false)}
        onDraftEdited={handleDraftEdited}
        onDraftRemoved={handleDraftRemoved}
      />
    </AppLayout>
  );
}